import React, { useState } from 'react';
import { CheckCircleOutline as CheckIcon } from '@mui/icons-material';
import { Lnk, BottomCta, usePageMeta } from './shared';

type Tier = {
  name: string;
  band: string;
  monthly?: number;
  annual?: number;
  custom?: boolean;
  featured?: boolean;
  cta: string;
  feats: string[];
};

const TIERS: Tier[] = [
  {
    name: 'Starter',
    band: 'For a single organization · up to 250 active members',
    monthly: 59,
    annual: 49,
    cta: 'Start free trial',
    feats: [
      'Home feed & member directory',
      'Events & RSVPs',
      'Messaging & groups',
      'Surveys & polls',
      'Newsletters',
      'Dues & payments',
      'Konklave app for iPhone & Android',
    ],
  },
  {
    name: 'Organization',
    band: 'For multi-chapter orgs, regions & councils · up to 2,500 active members',
    monthly: 299,
    annual: 249,
    featured: true,
    cta: 'Start free trial',
    feats: [
      'Everything in Starter',
      'Custom branding & colors',
      'Org hierarchy (regions, chapters)',
      'Voting & elections',
      'Fundraising & marketplace',
      'Advanced admin & roles',
      'Priority support',
      'Branded mobile app available',
    ],
  },
  {
    name: 'National',
    band: 'For national & international HQ · unlimited members',
    custom: true,
    cta: 'Talk to us',
    feats: [
      'Everything in Organization',
      'Single Sign-On (SSO)',
      'Multi-tenant branding across every level',
      'Dedicated branded mobile app',
      'Migration & onboarding',
      'Dedicated support & SLA',
    ],
  },
];

const ADDONS: { t: string; d: string }[] = [
  { t: 'Custom domain', d: 'Run on your own domain (yourorg.app) with managed SSL, instead of a konklave.app address.' },
  { t: 'Your own branded app', d: 'A dedicated app in the App Store and Google Play under your name and icon. One-time setup fee; ongoing maintenance is included in your plan.' },
  { t: 'White-glove migration', d: 'We help import your roster, history, and chapters so your community is live fast.' },
];

const Pricing: React.FC = () => {
  usePageMeta('Pricing');
  const [annual, setAnnual] = useState(true);

  return (
    <>
      <section className="page-head">
        <div className="container">
                    <h1>Simple pricing that grows with your community.</h1>
          <p>Start with a 14-day free trial. Plans scale with your active members, and you can cancel anytime.</p>
        </div>
      </section>

      <section className="page-body">
        <div className="container">
          <div className="center">
            <div className="pricing-toggle" role="group" aria-label="Billing period">
              <button type="button" aria-pressed={annual} className={annual ? 'active' : ''} onClick={() => setAnnual(true)}>
                Annual <span className="save">(2 months free)</span>
              </button>
              <button type="button" aria-pressed={!annual} className={!annual ? 'active' : ''} onClick={() => setAnnual(false)}>
                Monthly
              </button>
            </div>
          </div>

          <div className="pricing-grid">
            {TIERS.map((t) => (
              <div className={`price-card${t.featured ? ' featured' : ''}`} key={t.name}>
                {t.featured && <span className="price-badge">Most popular</span>}
                <h3>{t.name}</h3>
                <p className="tier-band">{t.band}</p>

                {t.custom ? (
                  <>
                    <div className="price-amount"><span className="amt">Custom</span></div>
                    <p className="price-sub">Tailored to your organization</p>
                  </>
                ) : (
                  <>
                    <div className="price-amount">
                      <span className="amt">${annual ? t.annual : t.monthly}</span>
                      <span className="per">/mo</span>
                    </div>
                    <p className="price-sub">{annual ? 'per month, billed annually' : 'per month, billed monthly'}</p>
                  </>
                )}

                <ul className="price-feats">
                  {t.feats.map((f) => (
                    <li key={f}><CheckIcon aria-hidden="true" /> <span>{f}</span></li>
                  ))}
                </ul>

                <Lnk
                  to={t.custom ? '/contact' : `/get-started?plan=${encodeURIComponent(t.name)}`}
                  className={`btn ${t.featured ? 'btn-primary' : 'btn-outline'}`}
                  style={{ justifyContent: 'center' }}
                >
                  {t.cta}
                </Lnk>
              </div>
            ))}
          </div>

          <p className="price-foot">
            Card processing on dues, event tickets, fundraising, and marketplace sales is paid by the member
            at checkout, so it adds no cost for your organization. Prices in USD.
          </p>

          <div className="group" style={{ marginTop: 56 }}>
            <div className="group-head"><h2>Add-ons</h2></div>
            <div className="addons">
              {ADDONS.map((a) => (
                <div className="addon" key={a.t}>
                  <h4>{a.t}</h4>
                  <p>{a.d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <BottomCta title="Not sure which plan fits? Let’s find the right one together." />
    </>
  );
};

export default Pricing;
